import { flocknoteKeys } from "Flocknote/API/keys";

import { hashQueryKey } from "@tanstack/query-core";
import { duplicateKeys } from "AccountMerge/duplicatesApi/keys";
import givingKeys from "Giving/API/keys";
import groupsKeys from "GroupsManagement/API/keys";
import { signupsListKeys } from "NavBar/SignupsAndEvents/API/keys";

export const CACHE_VERSION = 1;

export function getCacheKey() {
  const userID = window.data?.userID;
  return userID ? `fn_query_cache_${userID}` : null;
}

const CACHEABLE_KEYS = [
  flocknoteKeys.network(),
  flocknoteKeys.user(),
  flocknoteKeys.permissions(),
  groupsKeys.list(),
  signupsListKeys.signupsList(),
  givingKeys.getFundList(),
  duplicateKeys.getDuplicateCount(),
];

const CACHEABLE_KEY_HASHES = new Set(CACHEABLE_KEYS.map(hashQueryKey));

function isCacheableKey(queryKey) {
  return CACHEABLE_KEY_HASHES.has(hashQueryKey(queryKey));
}

/**
 * Hydrates React Query cache from localStorage on boot.
 * Call synchronously before any useQuery mounts.
 */
export function hydrateFromCache(queryClient) {
  try {
    const cacheKey = getCacheKey();
    if (!cacheKey) return;

    const raw = localStorage.getItem(cacheKey);
    if (!raw) return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(cacheKey);
      return;
    }

    if (parsed.v !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return;
    }

    const { data } = parsed;
    if (!data) return;

    Object.entries(data).forEach(([keyString, value]) => {
      const queryKey = JSON.parse(keyString);
      queryClient.setQueryData(queryKey, value);
      queryClient.invalidateQueries({ queryKey });
    });

    // flocknote.js group() blocks on window.data.groups. fetchGroups replaces this when it lands.
    const cachedGroups = data[JSON.stringify(groupsKeys.list())]?.groups;
    if (cachedGroups && !window.data?.groups) {
      window.fnReactBus.setGroups(cachedGroups, { fromCache: true });
    }
  } catch {
    // localStorage unavailable or other error — bail silently
  }
}

function readStoredData(cacheKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(cacheKey));
    return parsed?.v === CACHE_VERSION && parsed.data ? parsed.data : {};
  } catch {
    return {};
  }
}

function persistCache(queryClient) {
  try {
    const cacheKey = getCacheKey();
    if (!cacheKey) return;

    const stored = readStoredData(cacheKey);
    const data = {};
    let hasData = false;

    CACHEABLE_KEYS.forEach((key) => {
      const keyString = JSON.stringify(key);
      // Keep the stored value if this page never loaded the key. Otherwise
      // e.g. the member portal (no groups) wipes cached groups.
      const value = queryClient.getQueryData(key) ?? stored[keyString];
      if (value !== undefined) {
        data[keyString] = value;
        hasData = true;
      }
    });

    if (!hasData) return;

    localStorage.setItem(cacheKey, JSON.stringify({ v: CACHE_VERSION, data }));
  } catch {
    // Quota exceeded or unavailable — skip silently
  }
}

/**
 * Subscribes to React Query cache updates and persists cacheable keys
 * to localStorage with debouncing. Returns an unsubscribe function.
 */
export function startCachePersistence(queryClient) {
  let debounceTimer = null;
  let hasPendingWrite = false;

  const flushOnUnload = () => {
    if (!hasPendingWrite) return;
    clearTimeout(debounceTimer);
    persistCache(queryClient);
    hasPendingWrite = false;
  };

  window.addEventListener("pagehide", flushOnUnload);

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event?.type !== "updated" || event?.action?.type !== "success") return;
    if (!isCacheableKey(event.query.queryKey)) return;

    hasPendingWrite = true;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      persistCache(queryClient);
      hasPendingWrite = false;
    }, 500);
  });

  return () => {
    clearTimeout(debounceTimer);
    window.removeEventListener("pagehide", flushOnUnload);
    unsubscribe();
  };
}
